import { signInWithEmailAndPassword, signInWithPopup } from "firebase/auth";
import { useNavigate, useLocation } from "react-router-dom";
import { useState } from "react";
import { doc, getDoc, setDoc, updateDoc, arrayUnion } from "firebase/firestore";
import { db } from "../../../firebaseConfig.js";
import { defaultAgents, defaultDocs } from "../../constants/defaults";
import { serverTimestamp } from "firebase/firestore";

import "./SignIn.css";
import { auth, googleProvider } from "../../../firebaseConfig.js";
import CustomizedSnackbars from "../MaterialUI/CustomizedSnackbars";

const SignIn = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const urlEmail = params.get("email");
  const [email, setEmail] = useState(urlEmail || "");
  const [password, setPassword] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("error");
  const [loading, setLoading] = useState(false);

  const handleInviteLogic = async (user) => {
    const params = new URLSearchParams(location.search);
    const inviteOrgId = params.get("orgId");

    if (inviteOrgId) {
      const orgRef = doc(db, "organizations", inviteOrgId);
      const orgSnap = await getDoc(orgRef);
      if (orgSnap.exists()) {
        await updateDoc(orgRef, { members: arrayUnion(user.uid) });
        // Update user's active organizationId to switch them immediately
        await setDoc(doc(db, "users", user.uid), { organizationId: inviteOrgId }, { merge: true });
      }
    }
  };

  const signIn = (e) => {
    e.preventDefault();
    signInWithEmailAndPassword(auth, email, password)
      .then((userCredential) => {
        console.log(userCredential);
        const user = userCredential.user;
        // Ensure the user has a Firestore user document
        const userDocRef = doc(db, "users", user.uid);
        getDoc(userDocRef).then(async (docSnap) => {
          const isNewUser = !docSnap.exists();
          if (!isNewUser) {
            await handleInviteLogic(user); // Check for invite
            // Check for redirect param or state from ProtectedRoute
            const redirectPath = params.get("redirect") || location.state?.from?.pathname || "/home";
            navigate(redirectPath, { replace: true });
            return;
          }

          const firstName = user.firstName || user.displayName.split(" ")[0] || "";
          const lastName = user.lastName || user.displayName.split(" ").slice(1).join(" ") || "";
          const userData = {
            uid: user.uid,
            email: user.email,
            firstName: firstName,
            lastName: lastName,
            createdAt: serverTimestamp(),
          };

          if (isNewUser) {
            // Only add agents and docs for new users
            userData.agents = defaultAgents;
            userData.docs = defaultDocs;
          }

          // Store user data in Firestore using 'setDoc' and 'user.uid' as document ID
          return setDoc(userDocRef, userData, { merge: true }).then(() => {
            // console.log("Google user data saved to Firestore");
            navigate("/home");
            return;
          });
        });
      })
      .catch((error) => {
        setSnackbarMessage(error.message);
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        console.log(error);
      });
  };

  const signInWithGoogle = () => {
    setLoading(true); // Set loading to true when the Google sign-in starts
    setSnackbarOpen(false); // Close any open snackbars

    signInWithPopup(auth, googleProvider)
      .then((result) => {
        const user = result.user;
        const userDocRef = doc(db, "users", user.uid);
        getDoc(userDocRef).then(async (docSnap) => {
          const isNewUser = !docSnap.exists();
          if (!isNewUser) {
            await handleInviteLogic(user); // Check for invite
            // Check for redirect param or state from ProtectedRoute
            const redirectPath = params.get("redirect") || location.state?.from?.pathname || "/home";
            navigate(redirectPath, { replace: true });
            return;
          }
          const firstName = user.firstName || user.displayName.split(" ")[0] || "";
          const lastName = user.lastName || user.displayName.split(" ").slice(1).join(" ") || "";
          const userData = {
            uid: user.uid,
            email: user.email,
            firstName: firstName,
            lastName: lastName,
            createdAt: serverTimestamp(),
          };

          if (isNewUser) {
            // Only add agents and docs for new users
            userData.agents = defaultAgents;
            userData.docs = defaultDocs;
          }
          // Store user data in Firestore using 'setDoc' and 'user.uid' as document ID
          return setDoc(userDocRef, userData, { merge: true }).then(async () => {
            // console.log("Google user data saved to Firestore");
            await handleInviteLogic(user); // Ensure new Google users also process invites
            navigate("/home");
            return;
          });
        });
      })
      .catch((error) => {
        setSnackbarMessage(error.message);
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        console.log(error);
      })
      .finally(() => {
        setLoading(false); // Set loading to false once sign-in is successful
      });
  };

  return (
    <center>
      <div>
        <form className="sign-in-container" onSubmit={signIn}>
          <h4>Enter your login information</h4>
          <input className="field" type="email" placeholder="Enter your email" value={email} onChange={(e) => setEmail(e.target.value)}></input>
          <input
            className="field"
            type="password"
            placeholder="Enter your password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          ></input>

          <button className="button" type="submit">
            Log In
          </button>

          <button className="button" onClick={signInWithGoogle} style={{ color: "var(--Grey7)", background: "var(--Grey1)" }} disabled={loading}>
            Sign in with Google
            <svg xmlns="http://www.w3.org/2000/svg" height="40" width="40" viewBox="0 0 512 512" style={{ marginLeft: "12px" }}>
              <rect width="512" height="512" fill="#fff" rx="15%" />
              <path fill="#4285f4" d="M386 400c45-42 65-112 53-179H260v74h102c-4 24-18 44-38 57z" />
              <path fill="#34a853" d="M90 341a192 192 0 0 0 296 59l-62-48c-53 35-141 22-171-60z" />
              <path fill="#fbbc02" d="M153 292c-8-25-8-48 0-73l-63-49c-23 46-30 111 0 171z" />
              <path fill="#ea4335" d="M153 219c22-69 116-109 179-50l55-54c-78-75-230-72-297 55z" />
            </svg>
          </button>
        </form>

        <CustomizedSnackbars open={snackbarOpen} message={snackbarMessage} severity={snackbarSeverity} handleClose={() => setSnackbarOpen(false)} />
      </div>
    </center>
  );
};

export default SignIn;
